/**
 * WBC Service
 * Handles Westpac Business Online payment batch release
 * Framework C.4: Triggered after both Winnie and Tuo approval
 * Mocked implementation - ready for WBC API integration
 */

import { supabase } from '../lib/supabase';
import { executeTriggersForStatus } from './workflowTriggers';
import { ppsrGateForPayment } from '../lib/ppsrPaymentGate';
import {
  loadPayoutLetter,
  loadResolution,
  openResolution,
  payoutGate,
  splitLines,
  writePaymentLines,
} from './encumbranceService';

export interface WBCResult {
  success: boolean;
  message: string;
  details?: {
    batch_id?: string;
    payment_reference?: string;
  };
}

/**
 * Whether an approved finance payout letter is on file.
 *
 * The register saying there is money owing is not a reason to stop buying the car. It is
 * a reason to know who else has to be paid out of the same funds, and the letter from the
 * financier is the only document that says how much. Same document types and the same
 * decision field as the check in dealStatusService, so the two roads to Paid cannot hold
 * different opinions about the same deal.
 */
const payoutLetterApproved = async (dealId: string): Promise<boolean> => {
  const { data } = await supabase
    .from('dd_documents')
    .select('id, review_decision')
    .eq('deal_id', dealId)
    .in('document_type', ['PAYOUT', 'finance_payout_letter'])
    .is('deleted_at', null);

  return (data || []).some((doc: any) => doc.review_decision === 'approved');
};


/**
 * Turn an encumbered private-seller deal into two payment lines, or refuse it.
 *
 * Returns a refusal message, or null when the run is good to release. Writing the lines here
 * - at release, from the approved letter and the agreed price - rather than earlier means
 * they cannot go stale against a price that changed after somebody drafted them.
 */
const prepareEncumbranceSplit = async (
  paymentRunId: string,
  dealId: string,
  totalAmount: number,
): Promise<string | null> => {
  let resolution = null;
  let letter = null;
  try {
    resolution = await loadResolution(dealId);
    letter = await loadPayoutLetter(dealId);
  } catch (e) {
    /* Not being able to read the encumbrance record is not proof there isn't one, and this
       gate guards money leaving the business. Refuse and say why. */
    return 'The encumbrance record on this vehicle could not be read, so the payment was not released: ' +
      (e instanceof Error ? e.message : 'unknown error');
  }

  if (!resolution || resolution.resolutionPath !== 'PayoutLetterSplit') return null;

  const gate = payoutGate(resolution.resolutionPath, letter);
  if (!gate.ok) return gate.reason;

  const { data: deal } = await supabase
    .from('deals')
    .select('seller_name, seller_bsb, seller_account_number, seller_bank_account_name')
    .eq('id', dealId)
    .maybeSingle();

  const lines = splitLines({
    purchasePrice: totalAmount,
    payoutAmount: Number(letter?.payoutAmount) || 0,
    payoutRecipient: String(letter?.payoutRecipient || ''),
    payoutBsb: letter?.bankBsb,
    payoutAccount: letter?.bankAccount,
    payoutAccountName: letter?.bankName || letter?.payoutRecipient,
    sellerName: String((deal as Record<string, unknown> | null)?.seller_name || 'Seller'),
    sellerBsb: (deal as Record<string, unknown> | null)?.seller_bsb as string,
    sellerAccount: (deal as Record<string, unknown> | null)?.seller_account_number as string,
    sellerAccountName: (deal as Record<string, unknown> | null)?.seller_bank_account_name as string,
  });

  try {
    await writePaymentLines(paymentRunId, dealId, lines);
  } catch (e) {
    return 'The split payment lines could not be recorded, so the payment was not released: ' +
      (e instanceof Error ? e.message : 'unknown error');
  }
  return null;
};

export const wbcService = {
  /**
   * Release payment batch to Westpac
   * Framework C.4: Step 3 - triggered after Tuo approval
   * Moves deal status to paid
   */
  releasePaymentBatch: async (paymentRunId: string): Promise<WBCResult> => {
    try {
      // Fetch payment run
      const { data: paymentRun, error: fetchError } = await supabase
        .from('payment_runs')
        .select('id, deal_id, total_amount, winnie_review_complete, tuo_review_complete')
        .eq('id', paymentRunId)
        .single();

      if (fetchError || !paymentRun) {
        return {
          success: false,
          message: 'Payment run not found'
        };
      }

      // Framework C.4: Both approvals required
      if (!paymentRun.winnie_review_complete || !paymentRun.tuo_review_complete) {
        return {
          success: false,
          message: 'Both Winnie and Tuo approval required before WBC release'
        };
      }

      /* Winnie, 11 Sep, section 4: "payment release must independently verify a cleared
         PPSR check before processing".

         Independently means here, not upstream. This function writes deal_status straight
         to 'paid', so none of the preconditions in dealStatusService ever get a say, and
         the PPSR tick on the payment run is a person's account of what they saw rather
         than the register's. Every seller type and every status, including a Type 3/4/5
         deal that reached Committed on its tax invoice alone. */
      const ppsr = await ppsrGateForPayment(paymentRun.deal_id);
      if (!ppsr.passed) {
        return {
          success: false,
          message: ppsr.reason || 'The PPSR check on this vehicle has not cleared, so the payment was not released.'
        };
      }
      /* The register found finance owing. Open the resolution if one is not already open, so
         the split path exists before the gate below asks for its payout letter - Winnie's
         on_ppsr_check_result, hooked at the point the answer actually matters rather than at
         the moment the search returns, which happens server-side. */
      if (ppsr.encumbered) {
        const { data: sellerRow } = await supabase
          .from('deals')
          .select('seller_type, autograb_vin, vehicle_vin')
          .eq('id', paymentRun.deal_id)
          .maybeSingle();
        const st = sellerRow as Record<string, unknown> | null;
        await openResolution(
          paymentRun.deal_id,
          (st?.seller_type as string) || null,
          String(st?.autograb_vin || st?.vehicle_vin || ''),
        );
      }

      if (ppsr.encumbered && !(await payoutLetterApproved(paymentRun.deal_id))) {
        return {
          success: false,
          message: 'PPSR shows finance owing on this vehicle. A finance payout letter must be uploaded and approved in DD Review before the payment is released.'
        };
      }

      /* Winnie, 18 Sep - the encumbrance split.
       *
       * The check above proves a payout letter was approved. This one proves it carries the
       * two things the payment needs to be made AT ALL: what the financier is owed, and who
       * they are. An approved document with no figure on it cannot be paid from.
       *
       * Only on the PayoutLetterSplit path - a licensed seller clears its own encumbrance and
       * is paid the ordinary single payment. */
      const splitNote = await prepareEncumbranceSplit(paymentRunId, paymentRun.deal_id, Number(paymentRun.total_amount) || 0);
      if (splitNote) {
        return { success: false, message: splitNote };
      }

      // Mock WBC batch release
      const wbcResult = await mockWBC_BatchRelease(
        paymentRun.deal_id,
        paymentRun.total_amount
      );

      // Update payment run with WBC details
      const { error: updateError } = await supabase
        .from('payment_runs')
        .update({
          wbc_batch_id: wbcResult.batch_id,
          released_to_wbc_at: new Date().toISOString(),
          status: 'pending_wbc',
          payment_status: 'processing'
        })
        .eq('id', paymentRunId);

      if (updateError) throw updateError;

      // Move deal status to paid (Framework B.1)
      const { error: dealError } = await supabase
        .from('deals')
        .update({
          deal_status: 'paid',
          payment_run_id: paymentRunId,
          paid_at: new Date().toISOString()
        })
        .eq('id', paymentRun.deal_id);

      if (dealError) throw dealError;

      // Fire paid-stage automation (PPSR discharge, invoice, Xero). The deal was
      // moved to paid via a direct update above, so trigger the workflows here
      // (non-blocking) rather than relying on dealStatusService.
      executeTriggersForStatus(paymentRun.deal_id, 'paid').catch((e) =>
        console.error('paid triggers error:', e)
      );

      console.log(`[WBC] Payment batch released for deal ${paymentRun.deal_id}: ${wbcResult.batch_id}`);

      return {
        success: true,
        message: `Payment released to WBC (${wbcResult.batch_id})`,
        details: {
          batch_id: wbcResult.batch_id,
          payment_reference: wbcResult.payment_reference
        }
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      return {
        success: false,
        message: `WBC release failed: ${errorMsg}`
      };
    }
  },

  /**
   * Check payment batch status
   * Polls WBC API to confirm payment processed
   */
  checkBatchStatus: async (batchId: string): Promise<WBCResult> => {
    try {
      // Mock check status
      const status = await mockCheckWBC_Status(batchId);

      if (status.completed) {
        // Update payment run to completed
        const { data: paymentRun } = await supabase
          .from('payment_runs')
          .select('id')
          .eq('wbc_batch_id', batchId)
          .single();

        if (paymentRun) {
          await supabase
            .from('payment_runs')
            .update({
              status: 'completed',
              payment_status: 'completed',
              payment_processed_at: new Date().toISOString(),
              payment_reference: status.reference
            })
            .eq('id', paymentRun.id);
        }
      }

      return {
        success: true,
        message: `Batch status: ${status.status}`,
        details: {
          batch_id: batchId,
          payment_reference: status.reference
        }
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      return {
        success: false,
        message: `Status check failed: ${errorMsg}`
      };
    }
  },

  /**
   * Manual override - mark payment as complete if WBC sync fails
   * Framework C.4: Fallback if WBC API unreachable
   * Requires WBC reference number for audit trail
   */
  manualPaymentOverride: async (
    paymentRunId: string,
    wbcReferenceNumber: string,
    overriddenByUserId: string
  ): Promise<WBCResult> => {
    try {
      if (!wbcReferenceNumber) {
        return {
          success: false,
          message: 'WBC reference number required for manual override'
        };
      }

      const { data: paymentRun } = await supabase
        .from('payment_runs')
        .select('deal_id')
        .eq('id', paymentRunId)
        .single();

      if (!paymentRun) {
        return {
          success: false,
          message: 'Payment run not found'
        };
      }

      /* The manual override exists for the day the WBC API is unreachable, and it moves
         the same money. It gets the same PPSR gate: a bank being down is not a reason to
         pay for a car nobody has searched the register for. */
      const ppsr = await ppsrGateForPayment(paymentRun.deal_id);
      if (!ppsr.passed) {
        return {
          success: false,
          message: ppsr.reason || 'The PPSR check on this vehicle has not cleared, so the payment was not recorded.'
        };
      }
      if (ppsr.encumbered && !(await payoutLetterApproved(paymentRun.deal_id))) {
        return {
          success: false,
          message: 'PPSR shows finance owing on this vehicle. A finance payout letter must be uploaded and approved in DD Review before this payment can be recorded.'
        };
      }

      // Update payment run
      const { error: updateError } = await supabase
        .from('payment_runs')
        .update({
          status: 'completed',
          payment_status: 'completed',
          payment_processed_at: new Date().toISOString(),
          payment_reference: wbcReferenceNumber,
          wbc_batch_id: `MANUAL-${wbcReferenceNumber}`
        })
        .eq('id', paymentRunId);

      if (updateError) throw updateError;

      // Move deal to paid
      await supabase
        .from('deals')
        .update({
          deal_status: 'paid',
          paid_at: new Date().toISOString()
        })
        .eq('id', paymentRun.deal_id);

      // Fire paid-stage automation for the manual path too
      executeTriggersForStatus(paymentRun.deal_id, 'paid').catch((e) =>
        console.error('paid triggers error:', e)
      );

      // Log override to workflow_logs for audit trail
      await supabase
        .from('workflow_logs')
        .insert([
          {
            deal_id: paymentRun.deal_id,
            trigger_name: 'MANUAL_PAYMENT_OVERRIDE',
            trigger_status: 'success',
            success: true,
            details: {
              wbc_reference: wbcReferenceNumber,
              overridden_by: overriddenByUserId,
              reason: 'WBC sync manual fallback'
            },
            executed_at: new Date().toISOString()
          }
        ]);

      console.log(`[WBC] Manual override - payment marked complete: ${wbcReferenceNumber}`);

      return {
        success: true,
        message: 'Payment manually marked as complete',
        details: {
          payment_reference: wbcReferenceNumber
        }
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      return {
        success: false,
        message: `Manual override failed: ${errorMsg}`
      };
    }
  }
};

/**
 * Mock WBC batch release
 * In production: Call Westpac Business Online API
 */
const mockWBC_BatchRelease = async (
  dealId: string,
  amount: number
): Promise<{
  batch_id: string;
  payment_reference: string;
}> => {
  // Simulate WBC API call
  await new Promise(resolve => setTimeout(resolve, 500));

  const batchId = `WBC-${Date.now()}-${dealId.substring(0, 8)}`;
  const paymentRef = `WBC-PAY-${Date.now()}`;

  console.log('[MOCK WBC BATCH RELEASE]', {
    batch_id: batchId,
    amount_aud: amount,
    status: 'submitted',
    timestamp: new Date().toISOString()
  });

  return {
    batch_id: batchId,
    payment_reference: paymentRef
  };
};

/**
 * Mock WBC status check
 * In production: Poll Westpac API
 */
const mockCheckWBC_Status = async (
  batchId: string
): Promise<{
  status: string;
  completed: boolean;
  reference: string;
}> => {
  // Simulate API polling
  await new Promise(resolve => setTimeout(resolve, 200));

  // Mock: Always completed after 1 minute (in real life, poll periodically)
  const timeElapsed = Date.now() % (60 * 1000); // Simulate 60-second processing
  const completed = timeElapsed > 10000; // Mock: complete after 10 seconds

  console.log('[MOCK WBC STATUS CHECK]', {
    batch_id: batchId,
    status: completed ? 'completed' : 'processing',
    timestamp: new Date().toISOString()
  });

  return {
    status: completed ? 'completed' : 'processing',
    completed,
    reference: `WBC-COMPLETE-${batchId}`
  };
};

/**
 * INTEGRATION PLACEHOLDER
 * When Westpac Business Online credentials available:
 *
 * const westpac = require('westpac-api-client');
 *
 * // Create batch
 * const batch = await westpac.payments.createBatch({
 *   accountId: process.env.WBC_ACCOUNT_ID,
 *   payments: [
 *     {
 *       amount: paymentRun.total_amount,
 *       currency: 'AUD',
 *       description: `Payment for deal ${paymentRun.deal_id}`,
 *       beneficiary_account: buyerBankDetails
 *     }
 *   ]
 * });
 *
 * // Release batch
 * const release = await westpac.payments.releaseBatch(batch.id);
 *
 * return {
 *   batch_id: release.batch_id,
 *   payment_reference: release.transaction_reference
 * };
 */
